<template>
    <div class="release-diff-dialog">
        <div v-if="isLoading" class="loading-state">
            <ProgressSpinner />
            <p>Loading diff...</p>
        </div>

        <div v-else-if="error" class="error-state">
            <i class="pi pi-exclamation-triangle"></i>
            <p>{{ error }}</p>
            <Button label="Retry" icon="pi pi-refresh" @click="loadDiff" />
        </div>

        <div v-else-if="diff" class="diff-content">
            <div class="diff-summary">
                <div class="summary-stat">
                    <span class="stat-value">{{
                        diff.total_files_changed
                    }}</span>
                    <span class="stat-label">files changed</span>
                </div>
                <div class="summary-stat additions">
                    <span class="stat-value">+{{ diff.total_additions }}</span>
                    <span class="stat-label">additions</span>
                </div>
                <div class="summary-stat deletions">
                    <span class="stat-value">-{{ diff.total_deletions }}</span>
                    <span class="stat-label">deletions</span>
                </div>
            </div>

            <div
                v-for="domainDiff in diff.domain_diffs"
                :key="domainDiff.domain_id"
                class="domain-diff"
            >
                <div class="domain-header">
                    <h4>{{ domainDiff.domain_name }}</h4>
                    <div class="domain-stats">
                        <span class="files-changed"
                            >{{ domainDiff.files_changed }} files</span
                        >
                        <span class="additions"
                            >+{{ domainDiff.additions }}</span
                        >
                        <span class="deletions"
                            >-{{ domainDiff.deletions }}</span
                        >
                    </div>
                </div>

                <div class="changed-files">
                    <div
                        v-for="file in domainDiff.changed_files"
                        :key="file.path"
                        class="file-entry"
                    >
                        <span class="file-path">{{ file.path }}</span>
                        <span class="file-stats">
                            <span v-if="file.additions" class="additions"
                                >+{{ file.additions }}</span
                            >
                            <span v-if="file.deletions" class="deletions"
                                >-{{ file.deletions }}</span
                            >
                        </span>
                    </div>
                </div>
            </div>

            <div v-if="!diff.domain_diffs?.length" class="no-changes">
                <i class="pi pi-check-circle"></i>
                <p>No changes between these releases</p>
            </div>
        </div>
    </div>
</template>

<script setup lang="ts">
import type { DynamicDialogInstance } from "primevue/dynamicdialogoptions";
import ProgressSpinner from "primevue/progressspinner";
import { Ref, inject, onMounted, ref } from "vue";

import type { ReleaseDiffResponse } from "@/lib/openapi/gen/types.gen";
import { useReleaseStore } from "@/stores/release-store";

const dialogRef = inject<Ref<DynamicDialogInstance> | undefined>("dialogRef");
const releaseStore = useReleaseStore();

const isLoading = ref(false);
const error = ref<string>();
const diff = ref<ReleaseDiffResponse>();

// Get data passed to the dialog
const toRelease = dialogRef?.value?.data?.toRelease as string;
const fromRelease = dialogRef?.value?.data?.fromRelease as string | undefined;

const loadDiff = async () => {
    if (!toRelease) {
        error.value = "No release specified";
        return;
    }

    isLoading.value = true;
    error.value = undefined;

    try {
        const result = await releaseStore.getReleaseDiff(
            toRelease,
            fromRelease
        );
        if (result) {
            diff.value = result;
        } else {
            error.value = "Failed to load diff";
        }
    } catch (e) {
        console.error("Error loading diff:", e);
        error.value = "Failed to load diff";
    } finally {
        isLoading.value = false;
    }
};

onMounted(() => {
    loadDiff();
});
</script>

<style scoped lang="scss">
.release-diff-dialog {
    min-height: 200px;
}

.loading-state,
.error-state {
    display: flex;
    flex-direction: column;
    align-items: center;
    justify-content: center;
    padding: 2rem;
    text-align: center;
    gap: 1rem;

    i {
        font-size: 2rem;
        color: #dc3545;
    }
}

.diff-summary {
    display: flex;
    gap: 2rem;
    padding: 1rem;
    background: #f8f9fa;
    border-radius: 8px;
    margin-bottom: 1.5rem;
}

.summary-stat {
    display: flex;
    flex-direction: column;
    align-items: center;

    .stat-value {
        font-size: 1.5rem;
        font-weight: 600;
    }

    .stat-label {
        font-size: 0.875rem;
        color: #6c757d;
    }

    &.additions .stat-value {
        color: #28a745;
    }

    &.deletions .stat-value {
        color: #dc3545;
    }
}

.domain-diff {
    margin-bottom: 1.5rem;
    border: 1px solid #e9ecef;
    border-radius: 8px;
    overflow: hidden;
}

.domain-header {
    display: flex;
    justify-content: space-between;
    align-items: center;
    padding: 0.75rem 1rem;
    background: #f8f9fa;
    border-bottom: 1px solid #e9ecef;

    h4 {
        margin: 0;
        font-size: 1rem;
    }
}

.domain-stats {
    display: flex;
    gap: 1rem;
    font-size: 0.875rem;

    .files-changed {
        color: #6c757d;
    }

    .additions {
        color: #28a745;
    }

    .deletions {
        color: #dc3545;
    }
}

.changed-files {
    max-height: 300px;
    overflow-y: auto;
}

.file-entry {
    display: flex;
    justify-content: space-between;
    padding: 0.5rem 1rem;
    border-bottom: 1px solid #f1f3f4;
    font-family: monospace;
    font-size: 0.875rem;

    &:last-child {
        border-bottom: none;
    }

    &:hover {
        background: #f8f9fa;
    }
}

.file-path {
    color: #333;
}

.file-stats {
    display: flex;
    gap: 0.5rem;

    .additions {
        color: #28a745;
    }

    .deletions {
        color: #dc3545;
    }
}

.no-changes {
    display: flex;
    flex-direction: column;
    align-items: center;
    padding: 2rem;
    color: #6c757d;

    i {
        font-size: 2rem;
        color: #28a745;
        margin-bottom: 0.5rem;
    }
}
</style>
